import ChevronLeftRoundedIcon from '@mui/icons-material/ChevronLeftRounded';
import ChevronRightRoundedIcon from '@mui/icons-material/ChevronRightRounded';
import DeleteOutlineRoundedIcon from '@mui/icons-material/DeleteOutlineRounded';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import MoreHorizRoundedIcon from '@mui/icons-material/MoreHorizRounded';
import { Button, Skeleton } from '@mui/material';
import {
  addDays,
  eachDayOfInterval,
  format,
  getYear,
  isToday,
  isWithinInterval,
  parseISO,
} from 'date-fns';
import { SnackbarKey, useSnackbar } from 'notistack';
import React, {
  Fragment,
  ReactElement,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useSearchParams } from 'react-router-dom';
import { ReactComponent as PanelCollapseIcon } from '../../../assets/panel-collapse.svg';
import { DEFAULT_START_DATE } from '../../../constants';
import useAnchorMenu from '../../../hooks/useAnchorMenu';
import useMobileMediaQuery from '../../../hooks/useMobileMediaQuery';
import usePersistedState from '../../../hooks/usePersistedState';
import { useAppDispatch, useAppSelector } from '../../../redux/hooks';
import {
  selectCards,
  selectCurrentTrip,
  selectDeletedEvents,
} from '../../../redux/trip/trip.selectors';
import { clearDay, clearDeletedEvents, clearItinerary } from '../../../redux/trip/trip.slice';
import { DateISO, EventID, Trip, TripDay, TripEvent } from '../../../redux/trip/trip.types';
import { canEditTrip, getOrderedEvents } from '../../../redux/trip/trip.utils';
import { selectCurrentUser } from '../../../redux/user/user.selectors';
import { getISODate } from '../../../redux/utils';
import { Marker, MarkerType } from '../../../types/MapMarker';
import { getEmojiAndColorForCard } from '../../../utils/getEmojiAndColorForCard';
import { celsiusToFahrenheit, fetchWeather, WeatherDay } from '../../../utils/weather-utils';
import ConfirmDialog from '../../ConfirmDialog/ConfirmDialog';
import MapComponent from '../../Map/Map';
import DropdownMenu from '../../shared/DropdownMenu';
import CustomTooltip from '../../Tooltip/Tooltip';
import type { PendingDrop } from '../DnD/types';
import ItineraryEvent from './components/ItineraryEvent';
import ItineraryEvents from './components/ItineraryEvents';
import ItinerarySidebar from './components/ItinerarySidebar';
import ItinerarySidebarPlaceholder from './components/ItinerarySidebarPlaceholder';
import './Itinerary.scss';

interface ItineraryProps {
  trip: Trip;
  pendingDrop?: PendingDrop | null;
  onConfirmDrop?: () => void;
  onCancelDrop?: () => void;
}

function getDateISOToEventsMap(trip: Trip): Record<DateISO, TripEvent[]> {
  return Object.fromEntries(
    Object.entries(trip.days).map(([dateISO, day]) => [dateISO, getOrderedEvents(trip, day)])
  );
}

const defaultNumDays = 4;
// Must match `.itinerary-sidebar-panel { width }` in Itinerary.scss
const SIDEBAR_WIDTH = 300;
const WEATHER_DISCLAIMER =
  'Weather shows the current weekly forecast for your destination. Conditions may change — check a live weather service closer to your travel dates for the most accurate outlook.';

function Itinerary({ trip, pendingDrop, onConfirmDrop, onCancelDrop }: ItineraryProps) {
  // --- Selectors & hooks ---
  const dispatch = useAppDispatch();
  const isMobile = useMobileMediaQuery();
  const currentUser = useAppSelector(selectCurrentUser);
  const deletedEvents = useAppSelector(selectDeletedEvents);
  const cards = useAppSelector(selectCards);
  const currentTrip = useAppSelector(selectCurrentTrip);
  const { enqueueSnackbar, closeSnackbar } = useSnackbar();
  const [searchParams, setSearchParams] = useSearchParams();

  // --- State & refs ---
  // JSON-compatible with the previously stored 'true'/'false' strings.
  const [sidebarOpen, setSidebarOpen] = usePersistedState(`itinerary-sidebar:${trip.id}`, true);
  const [weatherByDate, setWeatherByDate] = useState<Record<string, WeatherDay>>({});
  const [weatherLoading, setWeatherLoading] = useState(false);
  const weatherFetchedAt = useRef<number>(0);
  const weatherFetchKey = useRef<string>('');
  const daysContainerRef = useRef<HTMLDivElement>(null);
  const hasScrolledToToday = useRef(false);
  // Day targeted for the "Clear Day" confirm dialog (null when closed).
  const [confirmClearDay, setConfirmClearDay] = useState<TripDay | null>(null);
  // Controls visibility of the "Clear Full Itinerary" confirm dialog.
  const [confirmClearAll, setConfirmClearAll] = useState(false);
  // Anchor element for the toolbar ⋯ options menu.
  const optionsMenu = useAnchorMenu();

  // --- Derived trip data ---
  // Use Redux trip data for real-time updates, fallback to prop for safety
  const activeTrip = currentTrip || trip;
  const canEdit = Boolean(canEditTrip(activeTrip, currentUser));
  const tripDateRange = useMemo(
    () => ({
      from: activeTrip.startDateISO ? parseISO(activeTrip.startDateISO) : DEFAULT_START_DATE,
      to: activeTrip.endDateISO
        ? parseISO(activeTrip.endDateISO)
        : addDays(DEFAULT_START_DATE, defaultNumDays - 1),
    }),
    [activeTrip.startDateISO, activeTrip.endDateISO]
  );
  const arrayOfDays = useMemo(
    () =>
      tripDateRange.from && tripDateRange.to
        ? eachDayOfInterval({ start: tripDateRange.from, end: tripDateRange.to })
        : [],
    [tripDateRange.from, tripDateRange.to]
  );
  // biome-ignore lint/correctness/useExhaustiveDependencies: only days/events affect the result
  const dateISOToEventsMap = useMemo(
    () => getDateISOToEventsMap(activeTrip),
    [activeTrip.days, activeTrip.events]
  );

  // --- Day view URL state ---
  const isDayView = searchParams.get('view') === 'day';
  const dateParam = searchParams.get('date');

  const activeDate = useMemo(() => {
    if (!arrayOfDays.length) {
      return null;
    }
    // Try the URL param first
    if (dateParam) {
      const parsed = parseISO(dateParam);
      const found = arrayOfDays.find((d) => getISODate(d) === dateParam);
      if (found) {
        return parsed;
      }
    }
    // Fallback: today if in range, else first day
    if (
      tripDateRange.from &&
      tripDateRange.to &&
      isWithinInterval(new Date(), { start: tripDateRange.from, end: tripDateRange.to })
    ) {
      return arrayOfDays.find((d) => isToday(d)) ?? arrayOfDays[0];
    }
    return arrayOfDays[0];
  }, [arrayOfDays, dateParam, tripDateRange.from, tripDateRange.to]);

  const activeDateISO = activeDate ? getISODate(activeDate) : null;
  const activeDateIndex = activeDate
    ? arrayOfDays.findIndex((d) => getISODate(d) === activeDateISO)
    : -1;

  const sortedMultidayEvents = useMemo(
    () =>
      [...Object.values(trip.events)]
        .filter((e): e is TripEvent & { eventLength: number } => Boolean(e.eventLength))
        .sort((a, b) => b.eventLength - a.eventLength),
    [trip.events]
  );

  // --- Callbacks ---
  const handleViewToggle = useCallback(
    (view: 'day' | 'full') => {
      setSearchParams((prev) => {
        if (view === 'day') {
          prev.set('view', 'day');
          if (!prev.get('date') && activeDateISO) {
            prev.set('date', activeDateISO);
          }
        } else {
          prev.delete('view');
          prev.delete('date');
        }
        return prev;
      });
    },
    [setSearchParams, activeDateISO]
  );

  const handlePrevDay = useCallback(() => {
    if (activeDateIndex <= 0) {
      return;
    }
    const prevDate = arrayOfDays[activeDateIndex - 1];
    setSearchParams((prev) => {
      prev.set('date', getISODate(prevDate));
      return prev;
    });
  }, [activeDateIndex, arrayOfDays, setSearchParams]);

  const handleNextDay = useCallback(() => {
    if (activeDateIndex < 0 || activeDateIndex >= arrayOfDays.length - 1) {
      return;
    }
    const nextDate = arrayOfDays[activeDateIndex + 1];
    setSearchParams((prev) => {
      prev.set('date', getISODate(nextDate));
      return prev;
    });
  }, [activeDateIndex, arrayOfDays, setSearchParams]);

  const handleClearDayConfirm = useCallback(() => {
    if (!confirmClearDay?.id) {
      setConfirmClearDay(null);
      return;
    }
    dispatch(
      clearDay({
        tripId: trip.id,
        dayId: confirmClearDay.id,
        dateISO: confirmClearDay.dateISO,
        eventIds: (confirmClearDay.eventOrder ?? []) as EventID[],
      })
    );
    setConfirmClearDay(null);
  }, [dispatch, trip.id, confirmClearDay]);

  const handleClearAllConfirm = useCallback(() => {
    dispatch(clearItinerary({ tripId: trip.id }));
    setConfirmClearAll(false);
  }, [dispatch, trip.id]);

  // --- Render data ---
  const dayViewMarkers = useMemo((): Marker[] => {
    if (!isDayView || !activeDateISO || !cards) {
      return [];
    }
    const day = activeTrip.days[activeDateISO];
    if (!day?.eventOrder) {
      return [];
    }
    let markerNumber = 0;
    return day.eventOrder.reduce<Marker[]>((acc, eventId) => {
      const event = activeTrip.events[eventId];
      if (!event) {
        return acc;
      }
      const card = activeTrip.cards[event.cardId];
      if (!card?.location) {
        return acc;
      }
      markerNumber += 1;
      const list = activeTrip.lists.find((l) => activeTrip.cardIdToListId[card.id] === l.id);
      acc.push({
        id: eventId, // use eventId for uniqueness (same card can appear multiple times)
        markerType: MarkerType.NUMBER,
        location: { lat: card.location.latitude, lng: card.location.longitude },
        data: {
          number: markerNumber,
          description: card.customName ?? card.name ?? '',
          color: list?.color ?? undefined,
          cardId: card.id, // stored for the click handler
        },
      });
      return acc;
    }, []);
  }, [
    isDayView,
    activeDateISO,
    cards,
    activeTrip.days,
    activeTrip.events,
    activeTrip.cards,
    activeTrip.lists,
    activeTrip.cardIdToListId,
  ]);

  // Derive eventId → markerNumber map from dayViewMarkers (marker.id is eventId, data.number is the position)
  const dayViewEventMarkerNumbers = useMemo((): Record<string, number> => {
    const map: Record<string, number> = {};
    for (const marker of dayViewMarkers) {
      map[marker.id] = (marker.data as { number: number }).number;
    }
    return map;
  }, [dayViewMarkers]);

  const [singleDayEvents, multiDaysEvents] = useMemo(() => {
    const events: ReactElement[] = [];
    const multiDays: ReactElement[] = [];
    if (!arrayOfDays.length) {
      return [events, multiDays];
    }

    const numDays = arrayOfDays.length;
    for (let index = 0; index < numDays; index++) {
      const date = arrayOfDays[index];
      const dateISO = getISODate(date);
      const col = index + 1;

      for (const eventItem of sortedMultidayEvents) {
        if (eventItem.startDateISO !== dateISO || !eventItem.id) {
          continue;
        }
        const eventLength = eventItem.eventLength;
        const span = col + eventLength <= numDays ? eventLength : -1;
        const { emoji, color, listLabel } = getEmojiAndColorForCard(
          eventItem.cardId,
          trip.lists,
          trip.dividers
        );
        multiDays.push(
          <div
            className='itinerary-multidays-events-item'
            key={eventItem.id}
            style={{
              gridColumn: `${col} / ${span < 0 ? span : `span ${span}`}`,
            }}
          >
            <ItineraryEvent
              tripId={trip.id}
              event={eventItem}
              emoji={emoji}
              color={color}
              listLabel={listLabel}
              canEdit={canEdit}
              card={trip.cards[eventItem.cardId]}
              eventLength={eventLength}
              isMultidayEvent
            />
          </div>
        );
      }

      const weather = weatherByDate[dateISO];
      events.push(
        <Fragment key={col}>
          <div
            className='itinerary-calendar-day-title itinerary-day-view-date'
            style={{ gridColumn: `${col} / span 1` }}
            data-today={isToday(date) ? 'true' : undefined}
          >
            <div className='itinerary-calendar-day-header-row'>
              {getYear(date) === getYear(DEFAULT_START_DATE) ? (
                <>
                  <span className='itinerary-day-view-date-top'>Day</span>
                  <span className='itinerary-day-view-date-num'>{col}</span>
                </>
              ) : (
                <>
                  <span className='itinerary-day-view-date-top'>{format(date, 'EEE')}</span>
                  <span className='itinerary-calendar-date-label'>
                    {format(date, 'MMM')}{' '}
                    <span className={isToday(date) ? 'itinerary-today-badge' : undefined}>
                      {format(date, 'd')}
                    </span>
                  </span>
                </>
              )}
            </div>
            {weatherLoading ? (
              <Skeleton
                variant='text'
                width={70}
                height={14}
                sx={{ borderRadius: 1 }}
              />
            ) : weather ? (
              <CustomTooltip
                title={weather.description}
                placement='bottom'
                arrow
              >
                <span className='itinerary-weather'>
                  <span className='itinerary-weather-emoji'>{weather.emoji}</span>{' '}
                  {celsiusToFahrenheit(weather.maxC)}°F / {weather.maxC}°C
                </span>
              </CustomTooltip>
            ) : null}
          </div>
          <ItineraryEvents
            tripId={trip.id}
            tripDay={trip.days[dateISO]}
            events={dateISOToEventsMap[dateISO]}
            col={col}
            canEdit={canEdit}
            pendingDrop={pendingDrop}
            onConfirmDrop={onConfirmDrop}
            onCancelDrop={onCancelDrop}
          />
        </Fragment>
      );
    }
    return [events, multiDays];
  }, [
    arrayOfDays,
    trip,
    dateISOToEventsMap,
    sortedMultidayEvents,
    canEdit,
    pendingDrop,
    onConfirmDrop,
    onCancelDrop,
    weatherByDate,
    weatherLoading,
  ]);

  // --- Side effects ---
  useEffect(() => {
    if (!deletedEvents?.length || !cards) {
      return;
    }
    const action = (snackbarId: SnackbarKey) => (
      <Button
        className='snackbar-dismiss-btn'
        onClick={() => closeSnackbar(snackbarId)}
      >
        undo*
      </Button>
    );
    deletedEvents.forEach((anEvent) => {
      enqueueSnackbar(`${cards[anEvent.cardId].name} added back to lists`, { action });
    });
    dispatch(clearDeletedEvents());
  }, [deletedEvents, cards, closeSnackbar, enqueueSnackbar, dispatch]);

  useEffect(() => {
    if (!activeTrip.location || !activeTrip.startDateISO || !activeTrip.endDateISO) {
      return;
    }
    const fetchKey = `${activeTrip.location.latitude},${activeTrip.location.longitude},${activeTrip.startDateISO},${activeTrip.endDateISO}`;
    if (fetchKey === weatherFetchKey.current && Date.now() - weatherFetchedAt.current < 3_600_000) {
      return;
    }
    setWeatherLoading(true);
    fetchWeather(
      activeTrip.location.latitude,
      activeTrip.location.longitude,
      activeTrip.startDateISO,
      activeTrip.endDateISO
    )
      .then((data) => {
        setWeatherByDate(data);
        weatherFetchedAt.current = Date.now();
        weatherFetchKey.current = fetchKey;
      })
      .catch(() => {
        // weather is enhancement-only; swallow silently
      })
      .finally(() => setWeatherLoading(false));
  }, [activeTrip.location, activeTrip.startDateISO, activeTrip.endDateISO]);

  useEffect(() => {
    if (isDayView) {
      // Reset so that returning to Full Itinerary will scroll to today again.
      hasScrolledToToday.current = false;
      return;
    }
    if (hasScrolledToToday.current || !daysContainerRef.current) {
      return;
    }
    const todayEl = daysContainerRef.current.querySelector(
      '[data-today="true"]'
    ) as HTMLElement | null;
    if (!todayEl) {
      return;
    }
    todayEl.scrollIntoView({
      behavior: 'instant' as ScrollBehavior,
      inline: 'center',
      block: 'nearest',
    });
    hasScrolledToToday.current = true;
  }, [isDayView]);

  // --- Render helpers ---
  // Day counter shown in both the sidebar-open row and the toolbar.
  const dayCounterLabel =
    isDayView && activeDateIndex >= 0 ? (
      <span className='itinerary-day-counter'>
        Day {activeDateIndex + 1} of {arrayOfDays.length}
      </span>
    ) : !isDayView && arrayOfDays.length > 0 ? (
      <span className='itinerary-day-counter'>{arrayOfDays.length} day trip</span>
    ) : null;

  // Weather for the currently active day (Day view only).
  const activeDayWeather = activeDateISO ? weatherByDate[activeDateISO] : undefined;

  // The active day's TripDay record, used by the "Clear Day" menu item in day view.
  const activeTripDay = activeDateISO ? activeTrip.days[activeDateISO] : undefined;
  // Whether the itinerary has any events at all — gates the ⋯ options button.
  const itineraryHasEvents = Object.values(activeTrip.events).length > 0;

  return (
    <div className={`itinerary-container${canEdit && !isMobile ? '' : ' no-sidebar'}`}>
      {canEdit && !isMobile && (
        <>
          <div className='itinerary-sidebar-outer'>
            <ItinerarySidebar
              trip={trip}
              canEdit={canEdit}
              open={sidebarOpen}
              onToggle={() => setSidebarOpen((p) => !p)}
            />
            {!Object.keys(trip.cardIdToListId).length && <ItinerarySidebarPlaceholder />}
          </div>
          <div
            className='itinerary-sidebar-divider'
            style={{ left: sidebarOpen ? `${SIDEBAR_WIDTH}px` : '0', opacity: sidebarOpen ? 1 : 0 }}
          />
        </>
      )}
      {canEdit && !isMobile && !sidebarOpen && (
        <div className='itinerary-sidebar-open-row'>
          <Button
            className='itinerary-sidebar-open-btn'
            size='small'
            endIcon={<PanelCollapseIcon style={{ transform: 'scaleX(-1)' }} />}
            onClick={() => setSidebarOpen(true)}
          >
            Lists
          </Button>
          <CustomTooltip
            title={WEATHER_DISCLAIMER}
            placement='bottom-start'
            arrow
          >
            <InfoOutlinedIcon className='itinerary-weather-info-icon' />
          </CustomTooltip>
          {dayCounterLabel}
        </div>
      )}
      <div
        className='itinerary-content'
        style={
          canEdit && !isMobile
            ? { marginLeft: sidebarOpen ? `calc(${SIDEBAR_WIDTH}px + 20px)` : '0' }
            : undefined
        }
      >
        {/* View toggle toolbar */}
        <div className='itinerary-view-toolbar'>
          {(!canEdit || isMobile || sidebarOpen) && (
            <CustomTooltip
              title={WEATHER_DISCLAIMER}
              placement='bottom-start'
              arrow
            >
              <InfoOutlinedIcon className='itinerary-weather-info-icon' />
            </CustomTooltip>
          )}
          {(!canEdit || isMobile || sidebarOpen) && dayCounterLabel}
          <div className='itinerary-view-toggle-group'>
            <button
              type='button'
              className={`itinerary-view-toggle-btn${isDayView ? ' active' : ''}`}
              onClick={() => handleViewToggle('day')}
            >
              Day
            </button>
            <span className='itinerary-view-toggle-divider' />
            <button
              type='button'
              className={`itinerary-view-toggle-btn${!isDayView ? ' active' : ''}`}
              onClick={() => handleViewToggle('full')}
            >
              Full Itinerary
            </button>
          </div>
          <div className='itinerary-view-toggle-group'>
            <button
              type='button'
              className='itinerary-view-toggle-btn'
              disabled
            >
              List
            </button>
            <span className='itinerary-view-toggle-divider' />
            <button
              type='button'
              className='itinerary-view-toggle-btn'
              disabled
            >
              Time
            </button>
          </div>
          <button
            type='button'
            className='itinerary-view-more-btn'
            disabled={!(canEdit && itineraryHasEvents)}
            onClick={optionsMenu.open}
            aria-label='More options'
          >
            <MoreHorizRoundedIcon fontSize='small' />
          </button>
        </div>
        <DropdownMenu
          {...optionsMenu.menuProps}
          className='itinerary-options-menu'
        >
          <DropdownMenu.Item
            tone='danger'
            icon={<DeleteOutlineRoundedIcon fontSize='small' />}
            hidden={!isDayView}
            onSelect={() => setConfirmClearDay(activeTripDay ?? null)}
          >
            Clear Day
          </DropdownMenu.Item>
          <DropdownMenu.Item
            tone='danger'
            icon={<DeleteOutlineRoundedIcon fontSize='small' />}
            hidden={isDayView}
            onSelect={() => setConfirmClearAll(true)}
          >
            Clear Full Itinerary
          </DropdownMenu.Item>
        </DropdownMenu>
        <div
          className='itinerary-days-container'
          ref={daysContainerRef}
        >
          {isDayView && activeDate && activeDateISO ? (
            <div className='itinerary-day-view-layout'>
              {/* Left: day header + events */}
              <div className='itinerary-day-view-column'>
                <div className='itinerary-day-view-header'>
                  <button
                    type='button'
                    className='itinerary-day-nav-btn'
                    onClick={handlePrevDay}
                    disabled={activeDateIndex <= 0}
                    aria-label='Previous day'
                  >
                    <ChevronLeftRoundedIcon />
                  </button>
                  <div className='itinerary-day-view-date'>
                    {getYear(activeDate) === getYear(DEFAULT_START_DATE) ? (
                      <span className='itinerary-day-view-date-label'>
                        Day {activeDateIndex + 1}
                      </span>
                    ) : (
                      <>
                        <span className='itinerary-day-view-date-top'>
                          {format(activeDate, 'EEE')}
                        </span>
                        <span className='itinerary-calendar-date-label'>
                          {format(activeDate, 'MMM')}{' '}
                          <span
                            className={isToday(activeDate) ? 'itinerary-today-badge' : undefined}
                          >
                            {format(activeDate, 'd')}
                          </span>
                        </span>
                      </>
                    )}
                    {weatherLoading ? (
                      <Skeleton
                        variant='text'
                        width={90}
                        height={16}
                        sx={{ borderRadius: 1 }}
                      />
                    ) : activeDayWeather ? (
                      <CustomTooltip
                        title={activeDayWeather.description}
                        placement='bottom'
                        arrow
                      >
                        <span className='itinerary-weather'>
                          <span className='itinerary-weather-emoji'>{activeDayWeather.emoji}</span>{' '}
                          {celsiusToFahrenheit(activeDayWeather.maxC)}°F / {activeDayWeather.maxC}°C
                        </span>
                      </CustomTooltip>
                    ) : null}
                  </div>
                  <button
                    type='button'
                    className='itinerary-day-nav-btn'
                    onClick={handleNextDay}
                    disabled={activeDateIndex >= arrayOfDays.length - 1}
                    aria-label='Next day'
                  >
                    <ChevronRightRoundedIcon />
                  </button>
                </div>

                {/* Multiday events for the active day */}
                {sortedMultidayEvents.some((e) => e.startDateISO === activeDateISO) && (
                  <div className='itinerary-day-view-multiday'>
                    {sortedMultidayEvents
                      .filter((e) => e.startDateISO === activeDateISO && e.id)
                      .map((eventItem) => {
                        const { emoji, color, listLabel } = getEmojiAndColorForCard(
                          eventItem.cardId,
                          trip.lists,
                          trip.dividers
                        );
                        return (
                          <ItineraryEvent
                            key={eventItem.id}
                            tripId={trip.id}
                            event={eventItem}
                            emoji={emoji}
                            color={color}
                            listLabel={listLabel}
                            canEdit={canEdit}
                            card={trip.cards[eventItem.cardId]}
                            eventLength={eventItem.eventLength}
                            isMultidayEvent
                          />
                        );
                      })}
                  </div>
                )}

                {/* Single-day events for the active day */}
                <ItineraryEvents
                  tripId={trip.id}
                  tripDay={trip.days[activeDateISO]}
                  events={dateISOToEventsMap[activeDateISO]}
                  col={1} /* col is used only for gridColumn in Full view; harmless in Day view */
                  canEdit={canEdit}
                  markerNumbers={dayViewEventMarkerNumbers}
                  pendingDrop={pendingDrop}
                  onConfirmDrop={onConfirmDrop}
                  onCancelDrop={onCancelDrop}
                />
              </div>

              {/* Right: map panel (hidden on mobile and narrow viewports) */}
              {!isMobile && (
                <div className='itinerary-day-view-map'>
                  <MapComponent
                    center={
                      activeTrip.location
                        ? { lat: activeTrip.location.latitude, lng: activeTrip.location.longitude }
                        : undefined
                    }
                    markers={dayViewMarkers}
                    autoFitMarkers
                    showCurrentLocation={false}
                    markerOnClick={(marker) =>
                      setSearchParams((prev) => {
                        const cardId = (marker.data as { cardId?: string }).cardId ?? marker.id;
                        prev.set('card', cardId);
                        return prev;
                      })
                    }
                  />
                </div>
              )}
            </div>
          ) : arrayOfDays ? (
            <div className='itinerary-calendar-container'>
              {multiDaysEvents.length > 0 && (
                <div
                  className='itinerary-multidays-events'
                  style={{
                    gridColumn: `1 / span ${arrayOfDays.length}`,
                    gridTemplateColumns: `repeat(${arrayOfDays.length}, 1fr)`,
                  }}
                >
                  {multiDaysEvents}
                </div>
              )}
              {singleDayEvents}
            </div>
          ) : null}
        </div>
      </div>

      <ConfirmDialog
        open={confirmClearDay !== null}
        title='Clear Day?'
        confirmLabel='Clear'
        onCancel={() => setConfirmClearDay(null)}
        onConfirm={handleClearDayConfirm}
        destructive
      >
        {confirmClearDay && (
          <>
            Remove all events from{' '}
            <strong>{format(parseISO(confirmClearDay.dateISO), 'EEEE, MMMM d')}</strong>? This
            cannot be undone.
          </>
        )}
      </ConfirmDialog>

      <ConfirmDialog
        open={confirmClearAll}
        title='Clear Full Itinerary?'
        confirmLabel='Clear all'
        onCancel={() => setConfirmClearAll(false)}
        onConfirm={handleClearAllConfirm}
        destructive
      >
        This will permanently remove all scheduled events from every day in your itinerary. This
        cannot be undone.
      </ConfirmDialog>
    </div>
  );
}

Itinerary.displayName = 'Itinerary';
export default Itinerary;
